import React, { useRef, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';
import { TextButton } from 'js/design_system/TextButton';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { trackClick } from 'js/utils/trackClick';
import { useElementImpression } from 'js/utils/impressions/useElementImpression';
import { openDownloadModal } from 'js/single_work/loswp/download_modal/openDownloadModal';

import {
  CHECKOUT_TRIGGER,
  IMPRESSION_SOURCE,
  PACK_CLICK_SOURCE,
  PDF_CLICK_SOURCE,
} from 'js/single_work/loswp/preview_gate/clickSources';

type PackRow = {
  id: number;
  title: string;
  image?: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
};

export type LoswpPackPitchProps = {
  workId: number;
  paperTitle: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
  thumbnailUrl?: string;
  attachmentId?: number;
  universityCount: number;
  // Our account-step page (Loswp::PackCheckoutController), which hands off to /checkout.
  packCheckoutUrl?: string | null;
  pack: {
    priceCopy?: string;
    totalCount: number;
    pageCount?: number;
    relatedWorks: PackRow[];
  };
};

const Section = styled.section`
  margin-bottom: var(--spacing-md-40, 40px);
  overflow: hidden;
  border: 1px solid var(--stroke-medium, #535366);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-white, #ffffff);
`;

// Tinted top band: eyebrow, headline, sub, and the count chips.
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  padding: var(--spacing-sm-24, 24px) var(--spacing-sm-32, 32px);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
  background: var(--background-light-blue, #eef2f9);

  @media (max-width: 767px) {
    padding: var(--spacing-sm-24, 24px);
  }
`;

const Eyebrow = styled.p`
  @import 'stylesheets/design_system/font';
  @include mono__small;
  margin: 0;
  color: var(--text-link, #0645b1);
  text-transform: uppercase;
`;

const Chips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
  margin: var(--spacing-xs-8, 8px) 0 0;
  padding: 0;
  list-style: none;
`;

const Chip = styled.li`
  @import 'stylesheets/design_system/font';
  @include mono__small;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-medium, #535366);
  border-radius: var(--spacing-xs-4, 4px);
  background: var(--background-white, #ffffff);
  color: var(--text-primary, #141413);
  text-transform: uppercase;
`;

const Contents = styled.div`
  padding: var(--spacing-sm-32, 32px);

  @media (max-width: 767px) {
    padding: var(--spacing-sm-24, 24px);
  }
`;

const ContentsHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-xs-16, 16px);
  margin-bottom: var(--spacing-xs-8, 8px);
`;

const ContentsTitle = styled.h3`
  @import 'stylesheets/design_system/font';
  @include body__medium-medium;
  margin: 0;
  color: var(--text-primary, #141413);
`;

const ContentsNote = styled.p`
  @import 'stylesheets/design_system/font';
  @include body__small;
  margin: 0;
  color: var(--text-secondary, #535366);
`;

// Scrollable viewport, not the whole list inline: the pack can run to ~20 rows and an inline list
// would push the CTA off-screen entirely. Fades at the bottom edge to signal there is more.
const RowsViewport = styled.div`
  position: relative;
  max-height: 296px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const RowsFade = styled.div`
  position: sticky;
  bottom: 0;
  height: 48px;
  margin-top: -48px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    var(--background-white, #ffffff) 90%
  );
  pointer-events: none;
`;

const Rows = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-xs-16, 16px) 0;
  border-top: 1px solid var(--stroke-light, #dddde2);
`;

// Ruled placeholder standing in for a page thumbnail, so a row without a cover still reads as a
// document rather than as a broken image.
const Thumb = styled.div`
  flex: 0 0 auto;
  width: 56px;
  height: 72px;
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--spacing-xs-4, 4px);
  background-color: var(--background-white, #ffffff);
  background-image: repeating-linear-gradient(
    to bottom,
    var(--stroke-light, #dddde2) 0,
    var(--stroke-light, #dddde2) 2px,
    transparent 2px,
    transparent 8px
  );
  background-position: 6px 10px;
  background-repeat: no-repeat;
  background-size: calc(100% - 12px) calc(100% - 20px);
`;

const ThumbImage = styled.img`
  flex: 0 0 auto;
  width: 56px;
  height: 72px;
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--spacing-xs-4, 4px);
  object-fit: cover;
`;

const RowBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RowTitle = styled.p`
  @import 'stylesheets/design_system/font';
  @include body__medium-medium;
  margin: 0;
  color: var(--text-primary, #141413);
`;

const RowMeta = styled.p`
  @import 'stylesheets/design_system/font';
  @include body__small;
  margin: var(--spacing-xs-4, 4px) 0 0;
  color: var(--text-secondary, #535366);
`;

const OwnBadge = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono__small;
  flex: 0 0 auto;
  padding: var(--spacing-xs-4, 4px) var(--spacing-xs-8, 8px);
  border-radius: var(--spacing-xs-4, 4px);
  background: var(--text-link, #0645b1);
  color: var(--text-white, #ffffff);
  text-transform: none;
  white-space: nowrap;
`;

const Remaining = styled.p`
  @import 'stylesheets/design_system/font';
  @include body__small;
  margin: var(--spacing-xs-16, 16px) 0 0;
  color: var(--text-secondary, #535366);
  text-align: center;
`;

const RemainingCount = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono__small;
  color: var(--text-primary, #141413);
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-sm-24, 24px) var(--spacing-sm-32, 32px);
  border-top: 1px solid var(--stroke-light, #dddde2);

  @media (max-width: 767px) {
    padding: var(--spacing-sm-24, 24px);
  }
`;

const TrustLine = styled(Body)`
  color: var(--text-secondary, #535366);
  text-align: center;
`;

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function metaLine(row: {
  publicationName?: string;
  publicationYear?: number;
}): string {
  return [row.publicationName, row.publicationYear].filter(Boolean).join(' · ');
}

// small_thumbnail_url 404s on plenty of works, and a broken-image glyph reads as a bug. Falls
// back to the ruled placeholder on error rather than showing one.
const RowThumb: React.FC<{ src?: string }> = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <Thumb />;
  return <ThumbImage src={src} alt="" onError={() => setFailed(true)} />;
};

// Rendered in the document slot, in place of the embedded viewer, directly under the locked
// panel. Inline rather than a modal on purpose: a modal's only offer disappears on dismiss, which
// leaves a gated visitor strictly worse off than control with nothing in exchange.
const LoswpPackPitch: React.FC<LoswpPackPitchProps> = (props) => {
  const {
    workId,
    paperTitle,
    authors,
    publicationName,
    publicationYear,
    thumbnailUrl,
    attachmentId,
    universityCount,
    packCheckoutUrl,
    pack,
  } = props;

  // Viewport-based, not on mount: this block sits below the fold on most viewports, so a
  // mount-time ping would count every gated pageview as a view of the offer and make
  // "seen and ignored" indistinguishable from "never scrolled to".
  const sectionRef = useRef<HTMLElement>(null);
  useElementImpression(sectionRef, {
    source: IMPRESSION_SOURCE,
    entity_id: workId,
    entity_type: ImpressionEntity.Work,
    // Liberal on purpose: impressions are a point-in-time record with no backfill, so every
    // dimension an analyst might segment on has to be here before the first bucketing lands.
    extra: {
      source_work_id: workId,
      pack_size: pack.totalCount,
      pack_page_count: pack.pageCount ?? null,
      row_count: pack.relatedWorks.length,
      has_price: Boolean(pack.priceCopy),
      has_thumbnail: Boolean(thumbnailUrl),
    },
  });

  const openDownload = (mode: 'pdf_only' | 'pdf_pack') => {
    trackClick({
      source: mode === 'pdf_pack' ? PACK_CLICK_SOURCE : PDF_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });

    // Pack path goes to our checkout-styled account step, which forwards to /checkout for
    // payment. Tracking fires first either way, so the click is attributed identically whether
    // this or the login-modal fallback runs.
    if (mode === 'pdf_pack' && packCheckoutUrl) {
      window.location.href = packCheckoutUrl;
      return;
    }

    void openDownloadModal({
      mode,
      workId,
      workTitle: paperTitle,
      authors,
      publicationName,
      publicationYear,
      thumbnailUrl,
      attachmentId,
      checkoutTrigger: CHECKOUT_TRIGGER,
      pack: {
        priceCopy: pack.priceCopy ?? '',
        totalCount: pack.totalCount,
        relatedWorks: pack.relatedWorks,
        // PreviewGateProps#serialized_row carries no relevancy score, so without this the modal
        // would print syntheticRelevancy(i) -- a positional placeholder -- as "97% relevancy" on
        // a paid offer. Set only here; other callers keep their current rendering.
        showRelevancy: false,
      },
    });
  };

  const totalPapers = pack.totalCount + 1;
  const hasRelated = pack.totalCount > 0;
  const shown = pack.relatedWorks;
  const remaining = Math.max(pack.totalCount - shown.length, 0);
  const universityCountCopy = universityCount.toLocaleString('en-US');
  // Falls back to an unpriced label rather than excluding the visitor: a price lookup that comes
  // back empty should change copy, not silently shrink the test population.
  const packCtaLabel = pack.priceCopy
    ? `Start your ${pack.priceCopy} trial and download the pack`
    : 'Start your trial and download the pack';

  return (
    <Section ref={sectionRef} aria-labelledby="loswp-pack-pitch-heading">
      <Header>
        <Eyebrow>Most popular on Academia</Eyebrow>
        <Heading
          as="h2"
          fontFamily="serif"
          size="lg"
          id="loswp-pack-pitch-heading"
        >
          {hasRelated
            ? `Get this paper and ${pack.totalCount} more, in one PDF pack`
            : 'Get this paper as a PDF'}
        </Heading>
        <Body>
          {hasRelated
            ? `Download it in one click instead of chasing ${plural(totalPapers, 'paywall')}.`
            : 'Download it in one click, with Premium.'}
        </Body>
        <Chips>
          <Chip>{plural(totalPapers, 'paper')}</Chip>
          {pack.pageCount != null && (
            <Chip>{plural(pack.pageCount, 'page')}</Chip>
          )}
        </Chips>
      </Header>

      <Contents>
        <ContentsHeader>
          <ContentsTitle>What&rsquo;s inside</ContentsTitle>
          {hasRelated && <ContentsNote>Ranked by relevance</ContentsNote>}
        </ContentsHeader>
        <RowsViewport>
          <Rows>
            <Row>
              <RowThumb src={thumbnailUrl} />
              <RowBody>
                <RowTitle>This paper &mdash; {paperTitle}</RowTitle>
                {metaLine({ publicationName, publicationYear }) && (
                  <RowMeta>
                    {metaLine({ publicationName, publicationYear })}
                  </RowMeta>
                )}
              </RowBody>
              <OwnBadge>Your paper</OwnBadge>
            </Row>
            {shown.map((row) => (
              <Row key={row.id}>
                <RowThumb src={row.image} />
                <RowBody>
                  <RowTitle>{row.title}</RowTitle>
                  {metaLine(row) && <RowMeta>{metaLine(row)}</RowMeta>}
                </RowBody>
              </Row>
            ))}
          </Rows>
          {hasRelated && <RowsFade />}
        </RowsViewport>
        {remaining > 0 && (
          <Remaining>
            <RemainingCount>+{remaining}</RemainingCount> more papers, all
            included in the pack
          </Remaining>
        )}
      </Contents>

      <Actions>
        <Button size="lg" onClick={() => openDownload('pdf_pack')}>
          {packCtaLabel}
        </Button>
        <TextButton size="lg" inline onClick={() => openDownload('pdf_only')}>
          Or download just this paper, free
        </TextButton>
        <TrustLine>
          Trusted by academics at {universityCountCopy} universities
        </TrustLine>
      </Actions>
    </Section>
  );
};

export default LoswpPackPitch;
